import cn from 'classnames';
import { ChangeEvent, FC, KeyboardEvent, useEffect, useMemo, useRef, useState } from 'react';
import { Text } from '../Text';

type ConfirmationCodeFieldProps = PropsWithClassName & {
  confirmationCodeLength: number;
  code: string;
  onChange: (code: string) => void;
  onFocus?: (isInvalid?: boolean) => void;
  disabled?: boolean;
  invalid?: boolean;
}

const ConfirmationCodeField: FC<ConfirmationCodeFieldProps> = ({ code, confirmationCodeLength, onChange, onFocus, disabled, invalid, className }) => {
  const [validationCode, setValidationCode] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const validationCodeInputRef = useRef<HTMLInputElement>(null);

  const onValidationCodeInputClick = () => validationCodeInputRef.current?.focus();
  const onValidationCodeInputFocus = () => {
    if (onFocus) {
      onFocus(invalid);
    }

    setIsFocused(true);
  };
  const onValidationCodeInputBlur = () => setIsFocused(false);

  const onValidationCodeInputKeyUp = (e: KeyboardEvent<HTMLInputElement>) => {
    if (disabled) return;

    if (e.key === 'Backspace') {
      setValidationCode(validationCode.slice(0, validationCode.length - 1));
    }
  };

  const onValidationCodeInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (disabled) return;

    const { value } = e.target as HTMLInputElement;
    const cleanValue = value.replace(/[^0-9]/gim, '');

    if (validationCode.length <= confirmationCodeLength) {
      setValidationCode((validationCode + cleanValue).slice(0, confirmationCodeLength));
    }
  };

  const selectedIndex = useMemo(() => {
    return validationCode.length < confirmationCodeLength ? validationCode.length : confirmationCodeLength - 1;
  }, [validationCode, confirmationCodeLength]);

  useEffect(() => {
    onChange(validationCode);
  }, [validationCode]);

  useEffect(() => {
    setValidationCode(code);
  }, [code]);

  return (
    <div className={cn('inline-flex justify-center items-center flex-col select-none', className)}>
      <div
        className="inline-flex justify-center items-center gap-xs"
        onClick={onValidationCodeInputClick}
      >
        {/* Create input cells */}
        {[...Array(confirmationCodeLength)].map((e, i) => (
          <div key={`cell-${i}`} className={cn("p-4xs rounded-6 overflow-hidden", i === selectedIndex ? "border-active" : "border-l0")}>
            <div key={`cell-${i}`} className={cn("w-7 h-7 p-2xs rounded-4 text-primary surface-l1 flex items-center justify-center cursor-pointer mr-2 last:mr-0")}>
              <Text as="span" type="p1-medium" className="text-primary">{validationCode.charAt(i)}</Text>
              {isFocused && i === selectedIndex && (
                <span className="w-px h-3 bg-blue-900 animate-caret-blink" aria-hidden="true" />
              )}
            </div>
          </div>
        ))}
        <input
          className="absolute font-size-p2 text-center bg-transparent outline-none opacity-0 w-0 h-0 appearance-none"
          ref={validationCodeInputRef}
          id="hidden-code-input"
          value=""
          onFocus={onValidationCodeInputFocus}
          onBlur={onValidationCodeInputBlur}
          onChange={onValidationCodeInputChange}
          onKeyUp={onValidationCodeInputKeyUp}
        />
      </div>
    </div >
  );
};

export default ConfirmationCodeField;
